/**
 * Construction des groupes vendeur + données expédition alignées sur CartPage
 * (packs / bundles : mêmes orderData pour l’API cart-line-items).
 */
import { parseShippingMethods, calculateShippingPrice } from './dates';
import { buildCartOrderDataForListing } from './cartOrderDataFromListing';

const canonicalizeShippingMethod = method => {
  if (!method || !method.id) {
    return null;
  }
  const priceOneItem = typeof method.priceOneItem === 'number' ? method.priceOneItem : 0;
  const priceAdditionalItems =
    typeof method.priceAdditionalItems === 'number' ? method.priceAdditionalItems : 0;
  const franco = typeof method.franco === 'number' ? method.franco : '';
  const delay = method.delay || '';
  const carrier = method.carrier || '';
  return `${method.id}:${priceOneItem}:${priceAdditionalItems}:${franco}:${delay}:${carrier}`;
};

const buildShippingGroupKeyFromMethods = methods => {
  if (!methods || methods.length === 0) {
    return null;
  }

  const canonicalParts = methods.map(canonicalizeShippingMethod).filter(Boolean).sort();

  return canonicalParts.length ? canonicalParts.join('|') : null;
};

const computeShippingDataForProvider = (items, listingsById) => {
  const shippingDataCache = {};

  const annotatedItems = items.map(item => {
    const listingId = item?.listingId?.uuid || item?.listingId;
    const listing = listingsById[listingId];

    if (!listing) {
      return { ...item, shippingGroupKey: null };
    }

    const inferred = buildCartOrderDataForListing(listing, item.quantity || 1);
    const merged = {
      ...item,
      ...(item.deliveryMethod ? {} : inferred.deliveryMethod ? { deliveryMethod: inferred.deliveryMethod } : {}),
      ...(item.shippingMethodId
        ? {}
        : inferred.shippingMethodId
        ? { shippingMethodId: inferred.shippingMethodId }
        : {}),
    };

    if (merged.deliveryMethod !== 'shipping') {
      return { ...merged, shippingGroupKey: null };
    }

    if (!shippingDataCache[listingId]) {
      const shippingMethods = parseShippingMethods(
        listing?.attributes?.publicData?.shippingMethods
      );
      const defaultShippingMethodId = shippingMethods?.[0]?.id || null;

      shippingDataCache[listingId] = {
        shippingMethods,
        defaultShippingMethodId,
      };
    }

    const shippingData = shippingDataCache[listingId];
    const shippingMethods = shippingData?.shippingMethods || [];

    if (!shippingMethods.length) {
      return { ...merged, shippingGroupKey: null };
    }

    const key = buildShippingGroupKeyFromMethods(shippingMethods);

    return {
      ...merged,
      shippingGroupKey: key,
    };
  });

  const shippingGroupsMap = new Map();

  annotatedItems.forEach(item => {
    if (!item.shippingGroupKey) {
      return;
    }

    const listingId = item?.listingId?.uuid || item?.listingId;
    const listing = listingsById[listingId];
    const shippingData = shippingDataCache[listingId];

    if (!listing || !shippingData || !shippingData.shippingMethods.length) {
      return;
    }

    if (!shippingGroupsMap.has(item.shippingGroupKey)) {
      shippingGroupsMap.set(item.shippingGroupKey, {
        key: item.shippingGroupKey,
        shippingMethods: shippingData.shippingMethods,
        defaultShippingMethodId: shippingData.defaultShippingMethodId,
        totalQuantity: 0,
        listingIds: [],
        productTitles: [],
      });
    }

    const entry = shippingGroupsMap.get(item.shippingGroupKey);
    entry.totalQuantity += item.quantity || 0;
    entry.listingIds.push(listingId);

    const title = listing?.attributes?.title;
    if (title && !entry.productTitles.includes(title)) {
      entry.productTitles.push(title);
    }
  });

  return {
    annotatedItems,
    shippingGroups: Array.from(shippingGroupsMap.values()),
  };
};

export const getSelectedShippingMethodIdForGroup = (group, shippingGroup) => {
  if (!shippingGroup) {
    return group.shippingMethodId || null;
  }

  const selections = group.shippingSelections || {};

  if (selections[shippingGroup.key]) {
    return selections[shippingGroup.key];
  }

  if (
    group.shippingGroups &&
    group.shippingGroups.length === 1 &&
    group.shippingMethodId &&
    shippingGroup.key === group.shippingGroups[0]?.key
  ) {
    return group.shippingMethodId;
  }

  return (
    shippingGroup.defaultShippingMethodId ||
    shippingGroup.shippingMethods?.[0]?.id ||
    null
  );
};

export const resolveShippingMethodIdForItem = (group, item) => {
  if (!item || item.deliveryMethod !== 'shipping') {
    return null;
  }

  const selections = group.shippingSelections || {};
  if (item.shippingGroupKey && selections[item.shippingGroupKey]) {
    return selections[item.shippingGroupKey];
  }

  if (
    group.shippingGroups &&
    group.shippingGroups.length === 1 &&
    group.shippingMethodId &&
    (!item.shippingGroupKey || item.shippingGroupKey === group.shippingGroups[0]?.key)
  ) {
    return group.shippingMethodId;
  }

  if (item.shippingMethodId) {
    return item.shippingMethodId;
  }

  if (!item.shippingGroupKey) {
    return group.shippingMethodId || null;
  }

  const shippingGroup =
    group.shippingGroups?.find(sg => sg.key === item.shippingGroupKey) || null;

  if (shippingGroup) {
    return (
      shippingGroup.defaultShippingMethodId ||
      shippingGroup.shippingMethods?.[0]?.id ||
      null
    );
  }

  return group.shippingMethodId || null;
};

export const calculateProviderShippingCost = (group, providerInfo) => {
  const providerSubtotal = providerInfo?.providerSubtotal || 0;
  const shippingGroups = group.shippingGroups || [];

  const computeCostForMethod = (method, quantity) => {
    if (!method) {
      return 0;
    }
    const qualifiesForFranco =
      typeof method.franco === 'number' && providerSubtotal >= method.franco;
    if (qualifiesForFranco) {
      return 0;
    }
    return calculateShippingPrice(method, quantity || 0);
  };

  if (shippingGroups.length === 0) {
    if (!group.shippingMethodId) {
      return 0;
    }

    const listingWithMethods = group.listings.find(listing => {
      const methods = parseShippingMethods(listing?.attributes?.publicData?.shippingMethods);
      return methods && methods.length > 0;
    });

    if (!listingWithMethods) {
      return 0;
    }

    const shippingMethods = parseShippingMethods(
      listingWithMethods?.attributes?.publicData?.shippingMethods
    );
    const selectedMethod =
      shippingMethods.find(method => method.id === group.shippingMethodId) || shippingMethods[0];

    const totalQuantity = group.items.reduce((sum, item) => sum + (item.quantity || 0), 0);
    return computeCostForMethod(selectedMethod, totalQuantity);
  }

  return shippingGroups.reduce((sum, shippingGroup) => {
    const methodId = getSelectedShippingMethodIdForGroup(group, shippingGroup);
    const method =
      shippingGroup.shippingMethods?.find(candidate => candidate.id === methodId) || null;
    return sum + computeCostForMethod(method, shippingGroup.totalQuantity || 0);
  }, 0);
};

/**
 * @param {object} cart - state.Cart.cart
 * @param {object} listingsById - map uuid -> listing entity
 */
export const buildProviderGroupsFromCart = (cart, listingsById) => {
  const providerIds = Object.keys(cart || {});

  return providerIds
    .map(pid => {
      const providerData = cart[pid] || {};
      const items = Array.isArray(providerData) ? providerData : providerData.items || [];
      const shippingMethodId = Array.isArray(providerData) ? undefined : providerData.shippingMethodId;
      const shippingSelections =
        Array.isArray(providerData) || !providerData.shippingSelections
          ? {}
          : providerData.shippingSelections;

      const validItems = items.filter(item => {
        const quantity = item.quantity;
        return typeof quantity === 'number' && !isNaN(quantity) && quantity > 0;
      });

      const { annotatedItems, shippingGroups } = computeShippingDataForProvider(
        validItems,
        listingsById
      );

      return {
        providerId: pid,
        shippingMethodId,
        shippingSelections,
        items: annotatedItems,
        listings: annotatedItems.map(item => listingsById[item.listingId?.uuid || item.listingId]),
        shippingGroups,
      };
    })
    .filter(group => group.items.length > 0);
};

export const buildProviderFrancoData = (providerGroups, listingsById) =>
  providerGroups.map(group => {
    const firstListing = group.listings.find(listing => listing?.id?.uuid);
    const profileFranco = firstListing?.author?.attributes?.profile?.publicData?.franco;

    const providerSubtotal = group.items.reduce((sum, item) => {
      const listing = listingsById[item.listingId?.uuid || item.listingId];
      const price = listing?.attributes?.price;
      const isValidPrice = price && typeof price.amount === 'number' && !isNaN(price.amount);
      const isValidQuantity =
        typeof item.quantity === 'number' && !isNaN(item.quantity) && item.quantity > 0;
      return sum + (isValidPrice && isValidQuantity ? price.amount * item.quantity : 0);
    }, 0);

    const shippingGroups = group.shippingGroups || [];
    const selections = group.shippingSelections || {};

    const hasMethodFreeShipping = shippingGroups.some(sg => {
      const selectedId =
        selections[sg.key] ||
        (group.shippingGroups?.length === 1 ? group.shippingMethodId : null) ||
        sg.defaultShippingMethodId ||
        sg.shippingMethods?.[0]?.id;

      const selectedMethod = sg.shippingMethods?.find(method => method.id === selectedId);
      return (
        selectedMethod &&
        typeof selectedMethod.franco === 'number' &&
        providerSubtotal >= selectedMethod.franco
      );
    });

    const hasProfileFreeShipping =
      typeof profileFranco === 'number' && providerSubtotal >= profileFranco;

    return {
      providerId: group.providerId,
      franco: profileFranco,
      providerSubtotal,
      hasFreeShipping: hasMethodFreeShipping || hasProfileFreeShipping,
    };
  });

/** Clé de groupe d’expédition par défaut (aligné sur CartPage / updateProviderShippingMethod) */
export const SHIPPING_GROUP_PROVIDER_KEY = 'provider';
